import { Flame, User } from "lucide-react"
import type { ReactNode } from "react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { COVER_ASPECT_CLASS } from "@/lib/mission-cover"
import { cn } from "@/lib/utils"

/**
 * OKS-468 — Brand-internal social-proof aggregate for a mission's cover-hero
 * card. **URL-only avatars — no names/handles/ids** (the aggregate must not
 * identify which crewmates posted). `views_count` is always null today (Crew
 * ingests no per-video view metric); the key is forward-compat.
 */
export interface CoverHeroSocialProof {
  posted_count: number
  trending: boolean
  avatars: readonly { url: string | null }[]
  views_count: number | null
}

export interface CoverHeroCardProps {
  /** Mission cover image. `null` → a branded gradient placeholder. */
  coverImageUrl: string | null
  /** Mission title, rendered in the card body below the cover. */
  title: string
  /** Brand-internal social proof overlaid on the cover. Omitted when absent
   *  or when `posted_count` is 0. */
  socialProof?: CoverHeroSocialProof
  /** Body meta line (e.g. "Take your time — no deadline"). */
  meta?: ReactNode
  /** Body CTA region (e.g. the Accept form). Optional so the card is reusable
   *  on read-only surfaces (S3 Crew Home feed — OKS-472). */
  children?: ReactNode
  className?: string
}

/**
 * OKS-468 — Reusable cover-hero mission card (§04). Brand-less, CTA-focused:
 * a full-bleed cover with a bottom scrim, a top-left FOMO chip, and an
 * overlaid social-proof footer; the title, meta line and an optional CTA sit
 * in the body below.
 *
 * Built as a standalone presentational component (NOT a variant of the
 * heavily state-driven `<CollabCard>`) so it composes cleanly on surfaces
 * that don't carry a CTA matrix — notably S3 Crew Home (OKS-472), which
 * reuses this card directly.
 *
 * Accessibility: the cover image carries the mission title as `alt` and is
 * non-focusable. The FOMO/social text is never color-only — it always pairs
 * an icon or literal words. White-on-scrim text rides the gradient for AA
 * contrast.
 *
 * Animation: a motion-safe entrance (≤480ms via `--animate-brand-intro`).
 * Under `prefers-reduced-motion` the card is fully visible with no transform.
 */
export function CoverHeroCard({
  coverImageUrl,
  title,
  socialProof,
  meta,
  children,
  className,
}: CoverHeroCardProps) {
  const { t } = useTranslation()
  const [coverErrored, setCoverErrored] = useState(false)
  const showCover = Boolean(coverImageUrl) && !coverErrored
  const showSocial = Boolean(socialProof && socialProof.posted_count > 0)

  return (
    <div
      data-testid="cover-hero-card"
      className={cn(
        "bg-card overflow-clip rounded-2xl border",
        "motion-safe:animate-brand-intro",
        className,
      )}
    >
      {/* Cover area — relative anchor for scrim + chip + social overlay.
          OKS-697: 3:2 (was fixed h-[8.25rem]) so the mission cover shows
          the same framed area as every other surface. */}
      <div
        className={cn(COVER_ASPECT_CLASS, "relative w-full overflow-hidden")}
      >
        {showCover ? (
          <img
            src={coverImageUrl ?? ""}
            alt={title}
            loading="lazy"
            decoding="async"
            onError={() => setCoverErrored(true)}
            className="h-full w-full object-cover"
          />
        ) : (
          <div
            className="from-primary/30 to-primary h-full w-full bg-gradient-to-br"
            data-testid="cover-hero-card-cover-fallback"
            aria-hidden="true"
          />
        )}

        {/* Bottom scrim — legibility for the overlaid chip + social text. */}
        <div
          className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/70 to-transparent"
          aria-hidden="true"
        />

        {/* FOMO chip (top-left). Trending → flame + "Trending · join them";
            otherwise a neutral "join them" cue (no flame, no scarcity). */}
        <span
          data-testid="cover-hero-card-fomo"
          data-trending={socialProof?.trending ? "true" : "false"}
          className={cn(
            "absolute top-3 left-3 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold",
            socialProof?.trending
              ? "bg-primary text-primary-foreground"
              : "bg-white/90 text-neutral-900",
          )}
        >
          {socialProof?.trending && (
            <Flame className="size-3.5" aria-hidden="true" />
          )}
          {socialProof?.trending
            ? t("wizard.welcome.handoff.fomo_trending")
            : t("wizard.welcome.handoff.fomo_join")}
        </span>

        {/* Social-proof footer (overlaid, bottom-left). Avatar stack + count.
            Brand-internal aggregate; avatar urls only, no identities. */}
        {showSocial && socialProof && (
          <div
            data-testid="cover-hero-card-social"
            className="absolute bottom-3 left-4 flex items-center gap-2"
          >
            <div className="flex items-center">
              {socialProof.avatars.map((avatar, index) => (
                <Avatar
                  // Index key is intentional — avatars are anonymous (no id).
                  key={index}
                  className="ring-background -ml-2.5 size-6 ring-2 first:ml-0"
                >
                  {avatar.url ? <AvatarImage src={avatar.url} alt="" /> : null}
                  <AvatarFallback className="bg-muted">
                    <User className="size-3" aria-hidden="true" />
                  </AvatarFallback>
                </Avatar>
              ))}
            </div>
            <span className="text-sm font-medium text-white">
              {t("wizard.welcome.handoff.social_posted", {
                count: socialProof.posted_count,
              })}
            </span>
          </div>
        )}
      </div>

      {/* Body — title, meta, optional CTA. */}
      <div className="flex flex-col gap-2.5 px-4 pt-3.5 pb-4">
        <h3
          className="text-base font-semibold"
          data-testid="cover-hero-card-title"
        >
          {title}
        </h3>
        {meta && (
          <p
            className="text-muted-foreground text-sm font-medium"
            data-testid="cover-hero-card-meta"
          >
            {meta}
          </p>
        )}
        {children}
      </div>
    </div>
  )
}

export default CoverHeroCard
